import {
	faExclamationCircle,
	faExclamationTriangle,
	faImage,
	faKey,
	faPuzzlePiece,
	faSpinner,
	faStream,
	faTrash
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMutation } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { apiClient } from "@/api/axiosInstance";
import { API_ENDPOINTS, resolveEndpoint } from "@/api/endpoints";
import { FlowAction } from "@/store/flowActions.store";
import { isValidBase64 } from "@/utils/base64";
import { ApiError, handleApiError } from "@/utils/errorHandler";
import { CreateFlowProps } from "./ViewFlow";
import { FlowTarget } from "./ViewFlowTraget";

export interface CredentialSchemaProperty {
	type: string;
	description?: string;
}

export interface CredentialSchemaDefinition {
	type: string;
	required?: string[];
	properties: Record<string, CredentialSchemaProperty>;
}

export const findActionName = (actionId: string, flowActions: FlowAction[]) => {
	const action = flowActions.find((action) => action.id === actionId);
	return action?.name || "Unknown Action";
};

const CreateFlowPanel: React.FC<CreateFlowProps> = ({
	flowTypeId,
	flowTargetId,
	onCreateFlow,
	flowDefinitions,
	flowActions,
	isLoading = false
}) => {
	const [credentials, setCredentials] = useState<Record<string, string>>({});
	const [credentialErrors, setCredentialErrors] = useState<
		Record<string, string>
	>({});
	const [credentialSchema, setCredentialSchema] =
		useState<CredentialSchemaDefinition | null>(null);
	const [logoImage, setLogoImage] = useState<string | null>(null);
	const [selectedDefinitions, setSelectedDefinitions] = useState<string[]>([]);
	const hasCalledApi = useRef(false);

	const handleCredentialChange = (key: string, value: string) => {
		setCredentials((prev) => ({ ...prev, [key]: value }));
		validateCredentialValue(key, value);
	};

	const getInputType = (property: CredentialSchemaProperty): string => {
		const type = property.type || "string";

		if (type === "string") {
			return "text";
		}
		if (type === "number" || type === "integer") {
			return "number";
		}
		if (type === "boolean") {
			return "checkbox";
		}

		return "text";
	};

	const isFieldRequired = (fieldName: string): boolean => {
		return credentialSchema?.required?.includes(fieldName) ?? false;
	};

	const validateCredentialValue = (key: string, value: string) => {
		if (!credentialSchema?.properties) return;

		const property = credentialSchema.properties[key];

		let error = "";

		if (isFieldRequired(key) && (!value || value.trim().length < 1)) {
			error = "This field is required";
		} else if (value) {
			const propType = property.type;

			switch (propType) {
				case "number":
					if (Number.isNaN(Number(value))) {
						error = "Must be a valid number";
					}
					break;
				case "integer":
					if (!Number.isInteger(Number(value)) || Number.isNaN(Number(value))) {
						error = "Must be a valid integer";
					}
					break;
				case "boolean":
					if (value !== "true" && value !== "false") {
						error = "Must be true or false";
					}
					break;
				case "string":
					if (key.toLowerCase().includes("url")) {
						try {
							new URL(value);
						} catch (_e) {
							error = "Must be a valid URL";
						}
					} else if (key.toLowerCase().includes("email")) {
						const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
						if (!emailRegex.test(value)) {
							error = "Must be a valid email address";
						}
					}
					break;
			}
		}

		setCredentialErrors((prev) => ({
			...prev,
			[key]: error
		}));

		return error === "";
	};

	const renderCredentialFields = () => {
		if (!credentialSchema || !credentialSchema.properties) {
			return (
				<div className="p-4 text-center text-gray-500">
					<FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
					No credential schema available
				</div>
			);
		}

		return Object.entries(credentialSchema.properties).map(
			([key, property]) => {
				const inputType = getInputType(property);

				return (
					<div key={key} className="mb-4">
						<label
							htmlFor={key}
							className="block text-sm font-medium text-gray-700 mb-1"
						>
							{key.charAt(0).toUpperCase() + key.slice(1)}
							{isFieldRequired(key) && (
								<span className="text-red-500 ml-1">*</span>
							)}
						</label>
						<div className="relative">
							{inputType === "checkbox" ? (
								<input
									type="checkbox"
									id={key}
									name={key}
									checked={credentials[key] === "true"}
									onChange={(e) =>
										handleCredentialChange(
											key,
											e.target.checked ? "true" : "false"
										)
									}
									className="h-4 w-4 text-blue-500 focus:ring-blue-500 border-gray-300 rounded"
									disabled={isLoading}
								/>
							) : (
								<input
									type={inputType}
									id={key}
									name={key}
									value={credentials[key] || ""}
									onChange={(e) => handleCredentialChange(key, e.target.value)}
									className={`w-full p-2 border ${
										credentialErrors[key] ? "border-red-500" : "border-gray-300"
									} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500`}
									placeholder={`Enter ${key}`}
									required={isFieldRequired(key)}
									disabled={isLoading}
								/>
							)}
							{credentialErrors[key] && (
								<p className="text-red-500 text-xs mt-1">
									<FontAwesomeIcon
										icon={faExclamationCircle}
										className="mr-1"
									/>
									{credentialErrors[key]}
								</p>
							)}
						</div>
						{property.description && (
							<p className="mt-1 text-xs text-gray-500">
								{property.description}
							</p>
						)}
					</div>
				);
			}
		);
	};

	const getTargetByIdMutation = useMutation({
		mutationFn: async (targetId: string) => {
			const { data } = await apiClient.get(
				resolveEndpoint(
					API_ENDPOINTS,
					["FLOW_TARGETS", "GET_BY_ID"],
					flowTypeId,
					targetId
				)
			);
			return data as FlowTarget;
		},
		onSuccess: (data: FlowTarget) => {
			if (data.credentialSchema) {
				setCredentialSchema(
					data.credentialSchema as unknown as CredentialSchemaDefinition
				);

				const initialCredentials: Record<string, string> = {};

				if (data.credentialSchema.properties) {
					Object.keys(data.credentialSchema.properties).forEach((key) => {
						initialCredentials[key] = "";
					});
				}
				setCredentials(initialCredentials);
			}

			if (data.logo && isValidBase64(data.logo)) {
				setLogoImage(data.logo);
			}
		},
		onError: (error: ApiError) => {
			handleApiError(error, "Failed to fetch target details");
		}
	});

	useEffect(() => {
		if (flowTargetId && !hasCalledApi.current) {
			hasCalledApi.current = true;
			getTargetByIdMutation.mutate(flowTargetId);
		}
	}, [flowTargetId, getTargetByIdMutation]);

	const handleDefinitionToggle = (definitionId: string, isChecked: boolean) => {
		setSelectedDefinitions((prev) => {
			if (isChecked) {
				return [...prev, definitionId];
			}
			return prev.filter((id) => id !== definitionId);
		});
	};

	const renderLogoPreview = (logo: string | null) => {
		if (!logo) return null;

		try {
			const isValidImage = isValidBase64(logo);

			if (!isValidImage) {
				return (
					<div className="mt-4 border rounded-lg p-4 bg-gray-50">
						<div className="flex justify-center">
							<img
								src="/assets/images/preview-not-found.png"
								alt="Preview not available"
								className="max-h-48 max-w-full object-contain opacity-50"
							/>
						</div>
					</div>
				);
			}

			return (
				<div className="mt-4 border rounded-lg p-4 bg-gray-50">
					<div className="flex justify-center">
						<img
							src={logo}
							alt="Target Logo Preview"
							className="max-h-48 max-w-full object-contain"
							onError={(e) => {
								e.currentTarget.src = "/assets/images/preview-not-found.png";
								e.currentTarget.className =
									"max-h-48 max-w-full object-contain opacity-50";
							}}
						/>
					</div>
				</div>
			);
		} catch (_error) {
			return (
				<div className="mt-4 border rounded-lg p-4 bg-gray-50">
					<div className="flex justify-center">
						<img
							src="/assets/images/preview-not-found.png"
							alt="Preview not available"
							className="max-h-48 max-w-full object-contain opacity-50"
						/>
					</div>
				</div>
			);
		}
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onloadend = () => {
			const base64String = reader.result as string;
			setLogoImage(base64String);
		};
		reader.readAsDataURL(file);
	};

	const handleSubmit = () => {
		let hasErrors = false;

		Object.entries(credentials).forEach(([key, value]) => {
			if (!validateCredentialValue(key, value)) {
				hasErrors = true;
			}
		});

		if (hasErrors) {
			return;
		}

		onCreateFlow({
			credential: credentials,
			flowDefinitions: selectedDefinitions.map((id) => ({ id })),
			logo: logoImage && isValidBase64(logoImage) ? logoImage : "logo"
		});

		if (logoImage && isValidBase64(logoImage)) {
			setLogoImage(logoImage);
		}
	};

	return (
		<div className="p-2">
			{/* Flow Actions Section */}
			<div className="mb-8 bg-white border border-gray-200 rounded-lg shadow-sm">
				<div className="p-4 border-b border-gray-200">
					<h3 className="font-medium text-lg flex items-center">
						<FontAwesomeIcon icon={faStream} className="mr-2 text-blue-500" />
						Actions
					</h3>
				</div>

				<div className="p-4">
					<div className="space-y-4">
						{flowDefinitions.map((definition) => (
							<div
								key={definition.id}
								className={`border rounded-md p-4 transition-colors ${
									selectedDefinitions.includes(definition.id)
										? "bg-green-50 border-green-200"
										: "bg-gray-50 border-gray-200"
								}`}
							>
								<div className="flex items-center justify-between">
									<div className="flex-grow">
										<h4 className="font-medium text-gray-800">
											{findActionName(definition.flowActionId, flowActions)}
										</h4>
									</div>
									<div className="flex items-center">
										<div className="relative inline-block w-12 align-middle select-none">
											<input
												type="checkbox"
												id={`toggle-${definition.id}`}
												className="sr-only"
												checked={selectedDefinitions.includes(definition.id)}
												onChange={(e) =>
													handleDefinitionToggle(
														definition.id,
														e.target.checked
													)
												}
												disabled={isLoading}
											/>
											<label
												htmlFor={`toggle-${definition.id}`}
												className={`block h-6 overflow-hidden rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${
													isLoading ? "opacity-50 cursor-not-allowed" : ""
												} ${
													selectedDefinitions.includes(definition.id)
														? "bg-green-600"
														: "bg-gray-300"
												}`}
											>
												<span
													className={`absolute inset-y-0 left-0 flex items-center justify-center w-6 h-6 rounded-full bg-white shadow transform transition-transform duration-200 ease-in-out ${
														selectedDefinitions.includes(definition.id)
															? "translate-x-6"
															: "translate-x-0"
													}`}
												/>
											</label>
										</div>
									</div>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>

			{/* Credentials Section */}
			<div className="mb-8 bg-white border border-gray-200 rounded-lg shadow-sm">
				<div className="p-4 border-b border-gray-200">
					<h3 className="font-medium text-lg flex items-center">
						<FontAwesomeIcon
							icon={faKey || faPuzzlePiece}
							className="mr-2 text-blue-500"
						/>
						Credentials
					</h3>
				</div>

				<div className="p-4">{renderCredentialFields()}</div>
			</div>

			{/* Logo Upload */}
			<div className="mb-8 bg-white border border-gray-200 rounded-lg shadow-sm">
				<div className="p-4 border-b border-gray-200">
					<h3 className="font-medium text-lg flex items-center">
						<FontAwesomeIcon icon={faImage} className="mr-2 text-blue-500" />
						Logo Image
					</h3>
				</div>

				<div className="p-4">
					<div className="mt-1 space-y-4">
						<div className="flex items-center justify-between space-x-2">
							<label className="w-full relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-700 focus-within:outline-none">
								<span className="w-full inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
									{logoImage ? "Change Logo" : "Upload Logo"}
								</span>
								<input
									type="file"
									className="sr-only"
									accept="image/*"
									onChange={handleFileChange}
									disabled={isLoading}
								/>
							</label>
							{logoImage && (
								<button
									type="button"
									onClick={() => setLogoImage(null)}
									className="p-1 bg-gray-100 rounded-full hover:bg-gray-200 focus:outline-none"
								>
									<FontAwesomeIcon icon={faTrash} className="text-red-500" />
								</button>
							)}
						</div>

						{renderLogoPreview(logoImage)}
					</div>
				</div>
			</div>

			{/* Action Buttons */}
			<div className="py-4 bg-white mb-[15px]">
				<button
					type="submit"
					className="w-full bg-blue-fynxt text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors flex justify-center items-center"
					disabled={isLoading}
					onClick={handleSubmit}
				>
					{isLoading ? (
						<>
							<FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
							Creating..."
						</>
					) : (
						"Create Flow"
					)}
				</button>
			</div>
		</div>
	);
};

export default CreateFlowPanel;
